// All operators as constants
export const EQ = 'EQ';
export const NEQ = 'NEQ';
export const GT = 'GT';
export const GTE = 'GTE';
export const LT = 'LT';
export const LTE = 'LTE';
export const LIKE = 'LIKE';
export const NOTLIKE = 'NOTLIKE';
export const BETWEEN = 'BETWEEN';
export const ANY = 'ANY';

// Operator type for conditional rules
export type Operator =
    | typeof EQ
    | typeof NEQ
    | typeof GT
    | typeof GTE
    | typeof LT
    | typeof LTE
    | typeof LIKE
    | typeof NOTLIKE
    | typeof BETWEEN
    | typeof ANY;

// Add this array for runtime use:
export const OperatorOptions: Operator[] = [EQ, NEQ, GT, GTE, LT, LTE, LIKE, NOTLIKE, BETWEEN, ANY];
// ConditionField represents the field in a rule condition
export type ConditionField =
    | 'unspecified'
    | 'plan_id'
    | 'user_id'
    | 'user_role'
    | 'user_status'
    | 'user_email'
    | 'custom';

// AccessCondition defines a single rule
export interface AccessCondition {
    Field?: ConditionField | number;
    CustomFieldName?: string;
    Operator?: Operator | number;
    Values?: string[];
    PrefetchedValues?: any[]; // This can be any type, depending on the field it stores values for Values ids
}

// AccessConditionGroup groups multiple Conditions using AND or OR logic
export interface AccessConditionGroup {
    Conditions?: AccessCondition[];
    CombineWithOR?: boolean;
}

// Rule is the top-level rule structure
export interface Rule {
    ConditionsGroups?: AccessConditionGroup[];
    CombineWithOR?: boolean;
}

// Function to map field names to numbers
function mapFieldToNumber(field: ConditionField): number {
    switch (field) {
        case 'unspecified':
            return 0;
        case 'plan_id':
            return 1;
        case 'user_id':
            return 2;
        case 'user_role':
            return 3;
        case 'user_status':
            return 4;
        case 'user_email':
            return 5;
        case 'custom':
            return 6;
        default:
            throw new Error(`Unknown field: ${field}`);
    }
}

// Function to map operators to numbers
function mapOperatorToNumber(operator: Operator): number {
    switch (operator) {
        case EQ:
            return 0;
        case NEQ:
            return 1;
        case GT:
            return 2;
        case GTE:
            return 3;
        case LT:
            return 4;
        case LTE:
            return 5;
        case LIKE:
            return 6;
        case NOTLIKE:
            return 7;
        case BETWEEN:
            return 8;
        case ANY:
            return 9;
        default:
            throw new Error(`Unknown operator: ${operator}`);
    }
}

// Function to map Rule to be passed to the backend where operators and fields are defined as numbers from 0 to n in grpc (protobuf)
export function mapRuleToBackend(rule: Rule): Rule {
    const mappedRule: Rule = {
        ConditionsGroups: rule.ConditionsGroups?.map((group) => ({
            Conditions: group.Conditions?.map((condition) => ({
                ...condition,
                Field: typeof condition.Field === 'string' ? mapFieldToNumber(condition.Field) : condition.Field,
                Operator:
                    typeof condition.Operator === 'string'
                        ? mapOperatorToNumber(condition.Operator)
                        : condition.Operator,
            })),
            CombineWithOR: group.CombineWithOR ? group.CombineWithOR : false,
        })),
        CombineWithOR: rule.CombineWithOR ? rule.CombineWithOR : false,
    };
    return mappedRule;
}

// Function to map Rule from the backend where operators and fields are defined as numbers from 0 to n in grpc (protobuf)
export function mapRuleFromBackend(rule: Rule): Rule {
    const operatorArray = ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE', 'LIKE', 'NOTLIKE', 'BETWEEN', 'ANY'] as Operator[];
    const mappedRule: Rule = {
        ConditionsGroups: rule.ConditionsGroups?.map((group) => ({
            Conditions: group.Conditions?.map((condition) => {
                let mappedOperator: Operator | undefined = condition.Operator as Operator;
                if (typeof condition.Operator === 'number' || typeof condition.Operator === 'undefined') {
                    mappedOperator = operatorArray[condition.Operator ? condition.Operator : 0] ?? 'EQ';
                }
                return {
                    ...condition,
                    Field:
                        typeof condition.Field === 'number'
                            ? ([
                                  'unspecified',
                                  'plan_id',
                                  'user_id',
                                  'user_role',
                                  'user_status',
                                  'user_email',
                                  'custom',
                              ][condition.Field] as ConditionField) ?? 'unspecified'
                            : condition.Field,
                    Operator: mappedOperator,
                };
            }),
            CombineWithOR: group.CombineWithOR ? group.CombineWithOR : false,
        })),
        CombineWithOR: rule.CombineWithOR ? rule.CombineWithOR : false,
    };
    return mappedRule;
}
